import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { palette } from '../../utils/palette';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  blogArticles,
  getFeaturedArticles,
  BlogArticle,
} from '../../data/blogArticles';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import SearchBar from '../../components/shared/SearchBar';

type BlogListScreenNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

const BlogListScreen = () => {
  const navigation = useNavigation<BlogListScreenNavigationProp>();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile, screenWidth } = useResponsiveLayout();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const featuredArticles = getFeaturedArticles();
  const categories = Array.from(
    new Set(blogArticles.map((article) => article.category))
  );

  const filteredArticles = blogArticles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );
    const matchesCategory =
      !selectedCategory || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const ArticleCard = ({ article }: { article: BlogArticle }) => {
    const handleBlogArticlePress = () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // Open in new tab on web
        window.open(`/blog/${article.id}`, '_blank');
      } else {
        // Navigate normally on mobile
        navigation.navigate('BlogArticle', { articleId: article.id });
      }
    };

    return (
      <TouchableOpacity
        style={[styles.articleCard, { backgroundColor: colors.card }]}
        onPress={handleBlogArticlePress}
        activeOpacity={0.7}
      >
        <Image
          source={{ uri: article.image }}
          style={styles.articleImage}
          resizeMode="cover"
        />
        <View style={styles.articleContent}>
          <View style={styles.articleHeader}>
            <View
              style={[
                styles.categoryBadge,
                { backgroundColor: colors.primary + '15' },
              ]}
            >
              <Text
                style={[styles.categoryBadgeText, { color: colors.primary }]}
              >
                {article.category}
              </Text>
            </View>
            {article.featured && (
              <View
                style={[
                  styles.featuredBadge,
                  { backgroundColor: colors.primary },
                ]}
              >
                <Ionicons name="star" size={12} color="white" />
              </View>
            )}
          </View>

          <Text
            style={[styles.articleTitle, { color: colors.text }]}
            numberOfLines={2}
          >
            {article.title}
          </Text>

          <Text
            style={[styles.articleExcerpt, { color: colors.secondary }]}
            numberOfLines={3}
          >
            {article.excerpt}
          </Text>

          <View style={styles.articleFooter}>
            <View style={styles.articleMeta}>
              <View style={styles.readTime}>
                <Ionicons
                  name="time-outline"
                  size={12}
                  color={colors.secondary}
                />
                <Text
                  style={[styles.readTimeText, { color: colors.secondary }]}
                >
                  {article.readTime} min
                </Text>
              </View>
            </View>

            {article.tags.length > 0 && (
              <View style={styles.tagsContainer}>
                {article.tags.slice(0, 3).map((tag, index) => (
                  <View
                    key={index}
                    style={[
                      styles.tag,
                      { backgroundColor: colors.primary + '10' },
                    ]}
                  >
                    <Text style={[styles.tagText, { color: colors.primary }]}>
                      #{tag}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: insets.bottom + 16, paddingTop: 12 },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={[
            styles.contentWrapper,
            {
              maxWidth: isMobile ? '100%' : Math.min(screenWidth * 0.85, 1100),
              alignSelf: 'center',
              width: '100%',
            },
          ]}
        >
          {/* Search Bar */}
          <SearchBar
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search articles..."
            containerStyle={styles.searchBarContainer}
          />

          {/* Category Filter */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.categoryScroll}
            contentContainerStyle={styles.categoryContainer}
          >
            <TouchableOpacity
              style={[
                styles.categoryChip,
                selectedCategory === null
                  ? { backgroundColor: colors.primary }
                  : { backgroundColor: colors.card },
              ]}
              onPress={() => setSelectedCategory(null)}
              activeOpacity={0.7}
            >
              <Text
                style={[
                  styles.categoryChipText,
                  selectedCategory === null
                    ? { color: palette.white }
                    : { color: colors.text },
                ]}
              >
                All
              </Text>
            </TouchableOpacity>
            {categories.map((category) => (
              <TouchableOpacity
                key={category}
                style={[
                  styles.categoryChip,
                  selectedCategory === category
                    ? { backgroundColor: colors.primary }
                    : { backgroundColor: colors.card },
                ]}
                onPress={() => setSelectedCategory(category)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.categoryChipText,
                    selectedCategory === category
                      ? { color: palette.white }
                      : { color: colors.text },
                  ]}
                >
                  {category}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Featured Section */}
          {featuredArticles.length > 0 &&
            !searchQuery &&
            selectedCategory === null && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Ionicons name="star" size={16} color={colors.primary} />
                  <Text style={[styles.sectionTitle, { color: colors.text }]}>
                    Featured Articles
                  </Text>
                </View>
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </View>
            )}

          {/* All Articles */}
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              {searchQuery || selectedCategory
                ? 'Search Results'
                : 'All Articles'}
            </Text>
            {filteredArticles.length === 0 ? (
              <View style={styles.emptyState}>
                <Ionicons
                  name="document-outline"
                  size={36}
                  color={colors.secondary}
                />
                <Text
                  style={[styles.emptyStateText, { color: colors.secondary }]}
                >
                  No articles found
                </Text>
              </View>
            ) : (
              <View style={styles.articlesGrid}>
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 12,
  },
  contentWrapper: {
    paddingHorizontal: 0,
  },
  searchBarContainer: {
    padding: 0,
    marginBottom: 12,
  },
  categoryScroll: {
    marginBottom: 14,
  },
  categoryContainer: {
    gap: 8,
    paddingRight: 12,
  },
  categoryChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  categoryChipText: {
    fontSize: 12,
    fontWeight: '600',
  },
  section: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  articlesGrid: {
    gap: 12,
  },
  articleCard: {
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 12,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  articleImage: {
    width: '100%',
    height: 160,
  },
  articleContent: {
    padding: 30,
  },
  articleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  categoryBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  featuredBadge: {
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  articleTitle: {
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 24,
    marginBottom: 6,
  },
  articleExcerpt: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 10,
  },
  articleFooter: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
    paddingTop: 10,
  },
  articleMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  authorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  authorAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
  },
  authorAvatarPlaceholder: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  authorInitials: {
    fontSize: 9,
    fontWeight: '700',
  },
  authorName: {
    fontSize: 11,
    fontWeight: '600',
  },
  readTime: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  readTimeText: {
    fontSize: 10,
    fontWeight: '500',
  },
  articleDate: {
    marginBottom: 6,
  },
  dateText: {
    fontSize: 10,
    fontWeight: '500',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
  },
  tag: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 5,
  },
  tagText: {
    fontSize: 9,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
  },
  emptyStateText: {
    fontSize: 14,
    marginTop: 8,
  },
});

export default BlogListScreen;
